/**
 * Reviews section — horizontal carousel (native scroll/swipe) + inline video play/pause.
 * Desktop: vertical scroll progress drives horizontal gallery movement while in view.
 * Safe for multiple sections on one page (isolated video state).
 */
(function () {
  'use strict';

  var DESKTOP_MIN_WIDTH_MQ = '(min-width: 770px)';

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function isDesktopViewport() {
    try {
      return window.matchMedia(DESKTOP_MIN_WIDTH_MQ).matches;
    } catch (e) {
      return false;
    }
  }

  function parseVideoId(url) {
    if (!url) return null;
    var yt = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (yt) return { type: 'youtube', id: yt[1] };
    var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) return { type: 'vimeo', id: vimeo[1] };
    return null;
  }

  function setToggleState(toggle, playing) {
    if (!toggle) return;
    toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      playing
        ? toggle.getAttribute('data-pause-label') || 'Pause video'
        : toggle.getAttribute('data-play-label') || 'Play video'
    );
    var playIcon = toggle.querySelector('.reviews__toggle-icon--play');
    var pauseIcon = toggle.querySelector('.reviews__toggle-icon--pause');
    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
  }

  function pauseCard(card) {
    if (!card) return;

    var video = card.querySelector('.reviews__inline-video');
    if (video && typeof video.pause === 'function') {
      video.pause();
      try { video.currentTime = 0; } catch (e) {}
    }

    var embed = card.querySelector('[data-reviews-video-embed]');
    if (embed) {
      embed.innerHTML = '';
      embed.hidden = true;
    }

    card.classList.remove('is-playing');
    setToggleState(card.querySelector('[data-reviews-video-toggle]'), false);
  }

  function playCard(card) {
    if (!card) return false;

    var toggle = card.querySelector('[data-reviews-video-toggle]');
    var video  = card.querySelector('.reviews__inline-video');
    var embed  = card.querySelector('[data-reviews-video-embed]');
    var url    = toggle ? toggle.getAttribute('data-video-url') : '';

    if (video) {
      var playPromise = video.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(function () { pauseCard(card); });
      }
    } else if (embed && url) {
      var parsed = parseVideoId(url);
      if (!parsed) return false;

      var src =
        parsed.type === 'youtube'
          ? 'https://www.youtube.com/embed/' + parsed.id + '?autoplay=1&playsinline=1'
          : 'https://player.vimeo.com/video/' + parsed.id + '?autoplay=1';

      embed.innerHTML =
        '<iframe src="' + src +
        '" allow="autoplay; encrypted-media; fullscreen" allowfullscreen title="Video"></iframe>';
      embed.hidden = false;
    } else {
      return false;
    }

    card.classList.add('is-playing');
    if (toggle) {
      if (!toggle.getAttribute('data-play-label')) {
        toggle.setAttribute('data-play-label', toggle.getAttribute('aria-label') || 'Play video');
      }
      toggle.setAttribute('data-pause-label', 'Pause video');
    }
    setToggleState(toggle, true);
    return true;
  }

  // ─── ReviewsSection ────────────────────────────────────────────────────────

  function ReviewsSection(root) {
    this.root    = root;
    this.track   = root.querySelector('[data-reviews-track]');
    this.prevBtn = root.querySelector('[data-reviews-prev]');
    this.nextBtn = root.querySelector('[data-reviews-next]');

    this._resizeObserver     = null;
    this._visibilityObserver = null;
    this._videoCards         = [];

    // Arrow update on manual track scroll
    this._onTrackScroll = this.updateArrows.bind(this);
    this._onKeydown     = this._handleKeydown.bind(this);

    // Scroll-drive state
    this._rafId              = null;   // continuous rAF handle
    this._boundTick          = null;
    this._scrollDriveEnabled = false;
    this._desktopMq          = null;
    this._onDesktopMqChange  = null;
    this._onWindowResize     = null;

    // Cached absolute geometry (avoids layout reads in the hot rAF path)
    this._sectionAbsTop  = 0;
    this._sectionHeight  = 0;
    this._maxTrackScroll = 0;
    this._lastScrollY    = -1;   // sentinel: forces first-frame update
    this._geometryDirty  = true;

    this.bind();
    this.measure();
    this.bindVideos();
    this.updateArrows();
    this._initScrollDrive();

    var self = this;

    if (typeof ResizeObserver !== 'undefined' && this.track) {
      this._resizeObserver = new ResizeObserver(function () {
        self.measure();
        self.updateArrows();
        self._geometryDirty = true;
        self._lastScrollY   = -1;
      });
      this._resizeObserver.observe(this.track);
    }

    root.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', function () {
          self.measure();
          self.updateArrows();
          self._geometryDirty = true;
          self._lastScrollY   = -1;
        }, { once: true, passive: true });
      }
    });
  }

  // ── bind UI controls ────────────────────────────────────────────────────

  ReviewsSection.prototype.bind = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () { self.scrollByCard(-1); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () { self.scrollByCard(1); });
    }

    if (this.track) {
      this.track.setAttribute('tabindex', '0');
      this.track.setAttribute('role', 'region');
      this.track.setAttribute('aria-label', 'Customer review gallery');
      this.track.addEventListener('scroll',  this._onTrackScroll, { passive: true });
      this.track.addEventListener('keydown', this._onKeydown);
    }
  };

  ReviewsSection.prototype.updateArrows = function () {
    if (!this.track) return;
    var maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
    if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
  };

  ReviewsSection.prototype._handleKeydown = function (event) {
    if (!event || !this.track) return;
    if (event.key === 'ArrowLeft')  { event.preventDefault(); this.scrollByCard(-1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); this.scrollByCard(1);  }
  };

  ReviewsSection.prototype.getGap = function () {
    if (!this.track) return 23;
    try { return parseInt(window.getComputedStyle(this.track).gap, 10) || 23; }
    catch (e) { return 23; }
  };

  ReviewsSection.prototype.scrollByCard = function (direction) {
    if (!this.track) return;
    var card = this.track.querySelector('[data-reviews-item]');
    if (!card) return;
    var amount = (card.offsetWidth || 0) + this.getGap();
    if (!amount) return;
    var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (typeof this.track.scrollBy === 'function') {
      this.track.scrollBy({ left: direction * amount, behavior: behavior });
    } else {
      this.track.scrollLeft += direction * amount;
    }
  };

  // ── video ───────────────────────────────────────────────────────────────

  ReviewsSection.prototype.bindVideos = function () {
    var self = this;
    this._videoCards = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-reviews-video-card]')
    );

    this._videoCards.forEach(function (card) {
      var toggle = card.querySelector('[data-reviews-video-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        if (card.classList.contains('is-playing')) { pauseCard(card); return; }
        self.pauseAllVideos();
        playCard(card);
      });
    });

    if (typeof IntersectionObserver === 'undefined' || !this._videoCards.length) return;

    this._visibilityObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) self.pauseAllVideos();
      });
    }, { threshold: 0.05 });

    this._visibilityObserver.observe(this.root);
  };

  ReviewsSection.prototype.pauseAllVideos = function () {
    this._videoCards.forEach(pauseCard);
  };

  // ── measure ─────────────────────────────────────────────────────────────

  ReviewsSection.prototype.measure = function () {
    if (!this.track) return;
    var firstItem = this.track.querySelector('[data-reviews-item]');
    if (firstItem) {
      this.root.style.setProperty('--reviews-card-w', firstItem.offsetWidth + 'px');
      this.root.style.setProperty('--reviews-card-h', firstItem.offsetHeight + 'px');
    }
  };

  // ── geometry cache ───────────────────────────────────────────────────────
  // Called once per rAF cycle when _geometryDirty is true.
  // Converts the section's viewport-relative rect into an absolute document
  // position so the hot path only does arithmetic — no DOM reads per frame.

  ReviewsSection.prototype._refreshGeometry = function () {
    if (!this.root || !this.track) return;
    var rect    = this.root.getBoundingClientRect();
    var scrollY = window.scrollY !== undefined ? window.scrollY : window.pageYOffset;
    this._sectionAbsTop  = rect.top + scrollY;
    this._sectionHeight  = rect.height;
    this._maxTrackScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    this._geometryDirty  = false;
  };

  // ── continuous rAF loop ──────────────────────────────────────────────────
  //
  // WHY a continuous loop instead of scroll-event → rAF:
  //
  //   The scroll-event → rAF pattern always introduces at least one frame of
  //   latency: the event fires after the browser has already updated scrollY,
  //   then we queue a rAF, which runs on the *next* paint. On trackpads the
  //   browser can coalesce or re-order events, so the value we read in the
  //   callback may already be stale by the time the frame paints.
  //
  //   A continuous loop reads window.scrollY at the very start of each paint
  //   callback — the same moment the browser uses it to composite the page.
  //   There is no event latency and no coalescing gap. The horizontal position
  //   is always computed from the value that will be visible in that frame.
  //
  //   The loop is cheap: when scrollY hasn't changed it exits after a single
  //   comparison and re-queues itself. No DOM reads or writes occur.

  ReviewsSection.prototype._tick = function () {
    if (!this._scrollDriveEnabled) {
      this._rafId = null;
      return;
    }

    // Re-queue immediately so we never miss a frame
    this._rafId = window.requestAnimationFrame(this._boundTick);

    // Rebuild geometry if layout has changed (resize, image load, etc.)
    if (this._geometryDirty) {
      this._refreshGeometry();
      this._lastScrollY = -1; // force position update after geometry refresh
    }

    var scrollY = window.scrollY !== undefined ? window.scrollY : window.pageYOffset;

    // Nothing has changed — skip all DOM work
    if (scrollY === this._lastScrollY) return;
    this._lastScrollY = scrollY;

    // ── compute progress ──────────────────────────────────────────────────
    var viewportH  = window.innerHeight || document.documentElement.clientHeight;
    var sectionTop = this._sectionAbsTop;
    var sectionBot = sectionTop + this._sectionHeight;

    // Section not yet entered viewport from below, or already scrolled past
    if (scrollY + viewportH <= sectionTop) return;
    if (scrollY >= sectionBot)             return;

    var totalTravel = this._sectionHeight + viewportH;
    if (totalTravel <= 0) return;

    var traveled = (scrollY + viewportH) - sectionTop;
    var progress = Math.max(0, Math.min(1, traveled / totalTravel));

    // ── apply ─────────────────────────────────────────────────────────────
    var maxScroll = this._maxTrackScroll;
    if (maxScroll <= 0) return;

    // Direct assignment (no smooth scroll, no easing) — mirrors page scroll 1:1
    this.track.scrollLeft = progress * maxScroll;
    this.updateArrows();
  };

  // ── scroll-drive lifecycle ───────────────────────────────────────────────

  ReviewsSection.prototype._startLoop = function () {
    if (this._rafId !== null) return; // already running
    this._boundTick = this._tick.bind(this);
    this._rafId = window.requestAnimationFrame(this._boundTick);
  };

  ReviewsSection.prototype._stopLoop = function () {
    if (this._rafId !== null) {
      window.cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._boundTick = null;
  };

  ReviewsSection.prototype._syncScrollDriveEnabled = function () {
    var shouldEnable = isDesktopViewport() && !prefersReducedMotion();
    this._scrollDriveEnabled = shouldEnable;

    if (shouldEnable) {
      this._geometryDirty = true;
      this._lastScrollY   = -1;
      this._startLoop();
    } else {
      this._stopLoop();
    }
  };

  ReviewsSection.prototype._initScrollDrive = function () {
    if (prefersReducedMotion()) return;

    var self = this;

    // Geometry must be rebuilt when the page layout changes
    this._onWindowResize = function () {
      self._geometryDirty = true;
      self._lastScrollY   = -1;
    };
    window.addEventListener('resize', this._onWindowResize, { passive: true });

    // Respond to viewport breakpoint changes (desktop ↔ mobile)
    try {
      this._desktopMq = window.matchMedia(DESKTOP_MIN_WIDTH_MQ);
      this._onDesktopMqChange = function () { self._syncScrollDriveEnabled(); };
      if (this._desktopMq.addEventListener) {
        this._desktopMq.addEventListener('change', this._onDesktopMqChange);
      } else if (this._desktopMq.addListener) {
        this._desktopMq.addListener(this._onDesktopMqChange);
      }
    } catch (e) {
      this._desktopMq = null;
    }

    this._syncScrollDriveEnabled();
  };

  ReviewsSection.prototype._destroyScrollDrive = function () {
    this._stopLoop();

    if (this._onWindowResize) {
      window.removeEventListener('resize', this._onWindowResize);
      this._onWindowResize = null;
    }

    if (this._desktopMq && this._onDesktopMqChange) {
      if (this._desktopMq.removeEventListener) {
        this._desktopMq.removeEventListener('change', this._onDesktopMqChange);
      } else if (this._desktopMq.removeListener) {
        this._desktopMq.removeListener(this._onDesktopMqChange);
      }
    }

    this._desktopMq          = null;
    this._onDesktopMqChange  = null;
    this._scrollDriveEnabled = false;
  };

  // ── destroy ──────────────────────────────────────────────────────────────

  ReviewsSection.prototype.destroy = function () {
    this.pauseAllVideos();
    this._destroyScrollDrive();

    if (this.track) {
      this.track.removeEventListener('scroll',  this._onTrackScroll);
      this.track.removeEventListener('keydown', this._onKeydown);
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }
  };

  // ─── Page-level init / Shopify section events ──────────────────────────

  function init(root) {
    if (!root || root.getAttribute('data-reviews-init') === 'true') {
      return root._reviewsSection || null;
    }
    root.setAttribute('data-reviews-init', 'true');
    var instance = new ReviewsSection(root);
    root._reviewsSection = instance;
    return instance;
  }

  function destroy(root) {
    if (!root) return;
    var instance = root._reviewsSection;
    if (instance && typeof instance.destroy === 'function') instance.destroy();
    delete root._reviewsSection;
    root.removeAttribute('data-reviews-init');
  }

  function initAll(scope) {
    var container = scope && scope.nodeType === 1 ? scope : document;
    container.querySelectorAll('[data-reviews-section]').forEach(init);
  }

  function destroyAll(scope) {
    var container = scope && scope.nodeType === 1 ? scope : document;
    container.querySelectorAll('[data-reviews-section]').forEach(destroy);
  }

  document.addEventListener('DOMContentLoaded',        function ()      { initAll(document);      });
  document.addEventListener('shopify:section:load',    function (event) { initAll(event.target);  });
  document.addEventListener('shopify:section:unload',  function (event) { destroyAll(event.target); });
  document.addEventListener('shopify:section:reorder', function ()      { initAll(document);      });

})();